/**
 * 消息气泡组件
 * 无边框、大留白风格
 * 支持显示建议操作（suggestedActions）
 * 支持满意/不满意反馈按钮
 *
 * 可访问性：
 * - aria-label 标识消息角色
 * - sr-only 提供屏幕阅读器文本
 */

import React from 'react';
import { clsx } from 'clsx';
import type { ChatMessage } from '../../services/types';
import { MarkdownContent } from './MarkdownContent';
import { FeedbackButtons } from './FeedbackButton';
import { formatTime } from '@/utils/date';

interface MessageBubbleProps extends ChatMessage {
  /** 建议操作点击回调 */
  onActionClick?: (action: string) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  role,
  content,
  timestamp,
  status,
  isStreaming,
  suggestedActions,
  // confidence 可用于未来显示置信度指示器
  onActionClick,
}) => {
  const isUser = role === 'user';
  const roleLabel = isUser ? '您' : '智能客服';

  // 处理建议操作点击
  const handleActionClick = (action: string) => {
    if (onActionClick) {
      onActionClick(action);
    }
  };

  return (
    <div
      className={clsx(
        'flex mb-4 flex-col group',
        isUser ? 'items-end' : 'items-start'
      )}
      role="article"
      aria-label={`${roleLabel}的消息`}
    >
      {/* 消息气泡 */}
      <div
        className={clsx(
          'max-w-[85%] px-5 py-3',
          isUser
            ? 'bg-[var(--bubble-user)] text-[var(--bubble-user-text)] rounded-2xl rounded-br-sm'
            : 'bg-[var(--bubble-ai)] text-[var(--bubble-ai-text)] rounded-2xl rounded-bl-sm'
        )}
      >
        {/* 屏幕阅读器文本 */}
        <span className="sr-only">{roleLabel}说：</span>

        {/* 消息内容 */}
        <div className="text-[15px] leading-relaxed">
          {isUser ? (
            // 用户消息：纯文本
            <>
              <div className="whitespace-pre-wrap">{content}</div>
              {isStreaming && (
                <span className="inline-block w-1.5 h-4 bg-[var(--accent-primary)] animate-pulse ml-1" />
              )}
            </>
          ) : (
            // AI 消息：Markdown 渲染
            <>
              <MarkdownContent content={content} />
              {isStreaming && (
                <span className="inline-block w-1.5 h-4 bg-[var(--accent-primary)] animate-pulse ml-1" />
              )}
            </>
          )}
        </div>

        {/* 时间戳 + 反馈按钮（同一行） */}
        {timestamp && (
          <div
            className={clsx(
              'flex items-center gap-2 mt-1.5',
              isUser ? 'text-blue-100' : 'text-[var(--text-muted)]'
            )}
          >
            <span
              className="text-xs"
              aria-label={`发送时间: ${formatTime(timestamp)}`}
            >
              {formatTime(timestamp)}
              {status === 'sending' && (
                <span className="sr-only">正在发送</span>
              )}
              {status === 'sending' && ' · 发送中...'}
              {status === 'failed' && (
                <span className="sr-only">发送失败</span>
              )}
              {status === 'failed' && ' · 发送失败'}
            </span>

            {/* 反馈按钮：仅 AI 消息、非流式状态时显示，默认隐藏，hover 时出现 */}
            {!isUser && !isStreaming && (
              <FeedbackButtons
                className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                onSatisfied={() => {
                  console.log('[MessageBubble] 用户点击满意');
                }}
                onUnsatisfied={() => {
                  console.log('[MessageBubble] 用户点击不满意');
                }}
                disabled={isStreaming}
              />
            )}
          </div>
        )}
      </div>

      {/* 建议操作（仅 AI 消息，非流式状态） */}
      {!isUser && !isStreaming && suggestedActions && suggestedActions.length > 0 && (
        <div className="flex items-center gap-[8px] mt-[8px] max-w-[85%] flex-wrap">
          <div className="flex flex-wrap gap-[8px]">
            {suggestedActions.map((action, index) => (
              <button
                key={index}
                onClick={() => handleActionClick(action)}
                className="px-[12px] py-[6px] bg-white/80 hover:bg-white text-[var(--text-primary)] text-[13px] leading-[18px] rounded-lg shadow-sm border border-gray-200 transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)]"
                aria-label={`建议操作: ${action}`}
              >
                {action}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
